// Mercapoli - City Context
// Maneja la ciudad seleccionada del usuario con persistencia
// ARQUITECTURA CIUDAD-CÉNTRICA: Modal obligatorio para primera visita
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ciudades as ciudadesApi } from '../lib/api';
import { useCart } from './CartContext';
import CitySelectorModal from '../components/CitySelectorModal';

const CityContext = createContext(null);

// Constante para la clave de localStorage
const STORAGE_KEY = 'mercapoli_ciudad';
const STORAGE_PAIS_KEY = 'mercapoli_pais';

// Rutas donde NO se debe mostrar el modal de ciudad
const EXCLUDED_ROUTES = [
  '/login',
  '/register',
  '/recuperar-password',
  '/registro-comercio',
  '/registro-rider',
  '/merchant',
  '/admin',
  '/rider',
  '/privacidad',
  '/terminos',
  '/eliminar-cuenta',
  '/menu',  // Menú público embebible (Fase 4.1)
  '/ciudad' // Landing dinámica por ciudad (Fase 5)
];

export const useCity = () => {
  const context = useContext(CityContext);
  if (!context) {
    throw new Error('useCity must be used within a CityProvider');
  }
  return context;
};

export const CityProvider = ({ children }) => {
  const [ciudad, setCiudadState] = useState(null);
  const [ciudades, setCiudades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pais, setPais] = useState('GT'); // País por defecto
  const [showCityModal, setShowCityModal] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname); // Inicializar con la ruta actual
  const { clearCart, isEmpty } = useCart();

  // Detectar cambios de ruta
  useEffect(() => {
    const updatePath = () => {
      const newPath = window.location.pathname;
      if (newPath !== currentPath) {
        setCurrentPath(newPath);
      }
    };
    
    // Escuchar cambios de ruta (popstate para navegación del browser)
    window.addEventListener('popstate', updatePath);
    
    // Intervalo para detectar cambios de URL en SPA (más confiable que MutationObserver)
    const interval = setInterval(updatePath, 100);
    
    return () => {
      window.removeEventListener('popstate', updatePath);
      clearInterval(interval);
    };
  }, [currentPath]);

  // Cargar ciudades y verificar si hay ciudad guardada
  useEffect(() => {
    initializeCity();
  }, []);

  const initializeCity = async () => {
    // Primero cargar ciudades disponibles
    await loadCiudades();
    
    // Luego verificar si hay ciudad guardada
    const savedCity = loadSavedCity();
    
    if (!savedCity) {
      // No hay ciudad guardada, mostrar modal obligatorio
      setShowCityModal(true);
    }
    
    setLoading(false);
  };

  const loadCiudades = async () => {
    try {
      const data = await ciudadesApi.getAll();
      setCiudades(data);
      return data;
    } catch (error) {
      console.error('Error loading cities:', error);
      return [];
    }
  };

  const loadSavedCity = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const savedPais = localStorage.getItem(STORAGE_PAIS_KEY);
      
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validar que la ciudad guardada es válida
        if (parsed && parsed.ciudad_id) {
          setCiudadState(parsed);
          setPais(parsed.pais || savedPais || 'GT');
          return parsed;
        }
      }
      return null;
    } catch (error) {
      console.error('Error loading saved city:', error);
      // Limpiar datos corruptos
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_PAIS_KEY);
      return null;
    }
  };

  // Cambiar ciudad - requiere confirmación si hay items en carrito
  const setCiudad = useCallback((nuevaCiudad, forceChange = false) => {
    if (!forceChange && !isEmpty && ciudad && ciudad.ciudad_id !== nuevaCiudad.ciudad_id) {
      // El componente que llama debe manejar la confirmación
      return false; // Indica que necesita confirmación
    }

    // Limpiar carrito si cambia de ciudad
    if (ciudad && ciudad.ciudad_id !== nuevaCiudad.ciudad_id) {
      clearCart();
    }

    setCiudadState(nuevaCiudad);
    setPais(nuevaCiudad.pais);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nuevaCiudad));
    localStorage.setItem(STORAGE_PAIS_KEY, nuevaCiudad.pais);
    
    // Cerrar el modal si está abierto
    setShowCityModal(false);
    
    return true; // Cambio exitoso
  }, [ciudad, isEmpty, clearCart]);

  // Manejar selección desde el modal
  const handleModalSelect = useCallback((selectedCity) => {
    setCiudad(selectedCity, true);
  }, [setCiudad]);

  // Abrir modal manualmente (para cambiar ciudad)
  const openCitySelector = useCallback(() => {
    setShowCityModal(true);
  }, []);

  // Obtener ciudades filtradas por país
  const getCiudadesByPais = useCallback((codigoPais) => {
    return ciudades.filter(c => c.pais === codigoPais && c.activa);
  }, [ciudades]);

  // Obtener símbolo de moneda actual
  const getMonedaSimbolo = useCallback(() => {
    return ciudad?.simbolo_moneda || 'Q';
  }, [ciudad]);

  // Obtener código de moneda actual
  const getMonedaCodigo = useCallback(() => {
    return ciudad?.moneda || 'GTQ';
  }, [ciudad]);

  // Formatear precio con moneda de la ciudad
  const formatPrice = useCallback((amount) => {
    const simbolo = ciudad?.simbolo_moneda || 'Q';
    return `${simbolo}${Number(amount).toFixed(2)}`;
  }, [ciudad]);

  // Verificar si la ruta actual está excluida del modal
  const isRouteExcluded = useCallback(() => {
    return EXCLUDED_ROUTES.some(route => currentPath.startsWith(route));
  }, [currentPath]);

  // Determinar si mostrar el modal
  const shouldShowModal = showCityModal && !ciudad && !isRouteExcluded();

  const value = {
    ciudad,
    ciudades,
    pais,
    loading,
    setCiudad,
    setPais,
    getCiudadesByPais,
    getMonedaSimbolo,
    getMonedaCodigo,
    formatPrice,
    reloadCiudades: loadCiudades,
    needsConfirmation: !isEmpty && ciudad !== null,
    openCitySelector,
    showCityModal
  };

  return (
    <CityContext.Provider value={value}>
      {children}
      {/* Modal obligatorio de selección de ciudad - solo en rutas permitidas */}
      <CitySelectorModal 
        isOpen={shouldShowModal} 
        onSelect={handleModalSelect}
      />
    </CityContext.Provider>
  );
};

export default CityContext;
